import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import type { CustomFieldPayload } from '@/api/custom_fields';
import type { CustomFieldEntity, CustomFieldScope } from '@/api/entity_types/CustomField';
import * as fieldTypes from './fieldTypes';

const deriveFieldKey = (name: string): string => name.trim().toLowerCase().replace(/\s+/g, '_');
const optionsToText = (options: string[]): string => options.join('\n');
const optionsFromText = (text: string): string[] =>
  text
    .split('\n')
    .map(option => option.trim())
    .filter(Boolean);

export type CustomFieldFormModel = Omit<CustomFieldPayload, 'options'> & { optionsText: string };

const blankForm = (): CustomFieldFormModel => ({
  name: '',
  key: '',
  field_type: 'short_text',
  scopes: ['contact:teammate', 'contact:candidate'],
  optionsText: '',
  sensitive: false,
  auto_collect_during_onboarding: false,
});

const toFormModel = (field: CustomFieldEntity): CustomFieldFormModel => ({
  name: field.name,
  key: field.key,
  field_type: field.field_type,
  scopes: [...field.scopes],
  optionsText: optionsToText(field.options),
  sensitive: field.sensitive,
  auto_collect_during_onboarding: field.auto_collect_during_onboarding,
});

export const useCustomFieldForm = () => {
  const existingCustomField = ref<CustomFieldEntity | null>(null);
  const form = ref<CustomFieldFormModel>(blankForm());
  const isSaving = ref(false);

  const derivedKey = computed(() => deriveFieldKey(form.value.name));
  const showOptions = computed(() => fieldTypes.find(form.value.field_type).hasOptions);
  const showAutoCollect = computed(() => form.value.scopes.includes('contact:teammate'));
  const parsedOptions = computed(() => (showOptions.value ? optionsFromText(form.value.optionsText) : []));

  const loadFrom = (field: CustomFieldEntity | null) => {
    existingCustomField.value = field;
    form.value = field ? toFormModel(field) : blankForm();
  };

  const toggleScope = (scope: CustomFieldScope, checked: boolean) => {
    const alreadySelected = form.value.scopes.includes(scope);
    if (checked && !alreadySelected) form.value.scopes = [...form.value.scopes, scope];
    if (!checked && alreadySelected) form.value.scopes = form.value.scopes.filter(current => current !== scope);
  };

  const buildPayload = (): CustomFieldPayload => ({
    name: form.value.name.trim(),
    key: existingCustomField.value ? form.value.key : derivedKey.value,
    field_type: form.value.field_type,
    scopes: form.value.scopes,
    options: parsedOptions.value,
    sensitive: form.value.sensitive,
    auto_collect_during_onboarding: showAutoCollect.value && form.value.auto_collect_during_onboarding,
  });

  const validationErrors = (): string[] => {
    const errors: string[] = [];
    if (form.value.name.trim() === '') errors.push('Field name is required');
    if (form.value.scopes.length === 0) errors.push('Select who this field applies to');
    if (showOptions.value && parsedOptions.value.length === 0) errors.push('Add at least one option');
    return errors;
  };

  const save = async (): Promise<boolean> => {
    const errors = validationErrors();
    if (errors.length) {
      toast.error('Could not save custom field', { description: errors.join(', ') });
      return false;
    }

    isSaving.value = true;
    try {
      const payload = buildPayload();
      const data = existingCustomField.value
        ? await Api.CustomFields.updateCustomField({ id: existingCustomField.value.id, body: payload })
        : await Api.CustomFields.createCustomField({ body: payload });

      if (data?.status === 'error') {
        toast.error('Could not save custom field', { description: data.message });
        return false;
      }

      toast.success(existingCustomField.value ? 'Custom field updated' : 'Custom field created');
      return true;
    } finally {
      isSaving.value = false;
    }
  };

  const remove = async (): Promise<boolean> => {
    if (!existingCustomField.value) return false;

    isSaving.value = true;
    try {
      await Api.CustomFields.deleteCustomField({ id: existingCustomField.value.id });
      toast.success('Custom field deleted');
      return true;
    } finally {
      isSaving.value = false;
    }
  };

  return {
    mode: computed<'new' | 'edit'>(() => (existingCustomField.value === null ? 'new' : 'edit')),
    form,
    isSaving,
    derivedKey,
    showOptions,
    showAutoCollect,
    loadFrom,
    toggleScope,
    save,
    remove,
  };
};
